import React, { useEffect, useState } from 'react';
import {
  InputText,
  PageFooter,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useDebounce,
  useFirstRender,
  useQueryParams,
} from '@insurancemenu/imenu-components-lib';
import SubscriberCard from 'components/common/election/cards/SubscriberCard';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import { useHistory, useParams } from 'react-router-dom';
import { smoothScrollToTop } from 'global/utils/smoothScrollToTop';
import ElectionSavingStatus from 'components/common/election/ElectionSavingStatus';
import ActionModal from 'components/common/modals/ActionModal';
import { ModalType } from 'global/utils/ModalsHelper';
import {
  EeClass,
  EmployeeInvitationStatusTypes,
  ProductInfo,
  Subscriber,
  SubscriberSERoles,
  DependentsChoice,
  UpdatedPlanInfo,
} from 'types/electionService';
import { createSubscriberPresenter } from 'global/utils/election/electionPresenters';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import SubscriberStatus from 'components/common/election/SubscriberStatus';
import DynamicFormModalDEPRECATED from 'components/common/modals/DynamicFormModal_DEPRECATED';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { getClassesAvailableForSelection } from 'global/services-helpers/electionService';
import ConfirmationBlob from 'components/common/general/ConfirmationBlob';
import { HeaderSection } from 'components/common/general/CollectionManagementPanel/HeaderSection';
import ElectionsEmployeesHeader from '../ElectionsEmployeesHeader';
import {
  createStyles,
  ElectionsContentWrapperStyled,
  ElectionsStickyBarStyled,
  ElementsQuantityStyled,
  QuantityStyled,
  SearchInputWrapperStyled,
} from './styles';
import EndOpenEnrollmentModal from '../EndOpenEnrollmentModal';
import EditPrimaryModal from '../../common/election/EditPrimaryModal';
import InviteModal from '../InviteModal';
import ManageDependentsModal from '../../common/election/ManageDependentsModal';
import { SUBSCRIBERS_TABLE_ID } from '../constants';

interface EditableElectionsContentProps {
  initialLoading: boolean;
}

interface SubscriberStatusProps {
  subscriber: Subscriber;
}

const SubscriberStatusWithInvitationDate: React.FC<SubscriberStatusProps> = ({ subscriber }) => {
  const classes = createStyles();

  const { selfEnrollStatus, subSERole, seInviteDate } = subscriber;
  const isStatusVisible = selfEnrollStatus ? ['INVITED', 'ELECTED', 'REGISTERED'].includes(selfEnrollStatus) : false;
  return (
    <div className={classes.statusWrapper}>
      {(subSERole === SubscriberSERoles.EMPLOYER || isStatusVisible) && (
        <SubscriberStatus status={selfEnrollStatus as EmployeeInvitationStatusTypes} subSERole={subSERole} />
      )}
      {selfEnrollStatus === EmployeeInvitationStatusTypes.INVITED &&
        subSERole !== SubscriberSERoles.EMPLOYER &&
        seInviteDate && <p className={classes.invitationDate}>{normDateToShortAmericanDate(seInviteDate)}</p>}
    </div>
  );
};

const EditableElectionsContent = observer(({ initialLoading }: EditableElectionsContentProps): JSX.Element => {
  const history = useHistory();
  const { periodId } = useParams<{ periodId: string }>();
  const [isEndOpenEnrollmentModalOpen, setIsEndOpenEnrollmentModalOpen] = useState(false);
  // This value is needed to display changes in the search input immediately,
  // but the request will use the property from the store, which is set after the delay
  const [searchName, setSearchName] = useState('');

  const firstRender = useFirstRender();

  const query = useQueryParams();
  const groupId = query.get('groupId') || '';

  const {
    employeeElectionsStore: {
      loading,
      subscribers,
      isElectionWithSubs,
      isElectionCompleted,
      savingStatus,
      filter,
      sortOrder,
      sortBy,
      searchValue,
      statusesFilter,
      classesFilter,
      totalPages,
      totalElements,
      currentPage,
      enrollmentPeriod,
      products,
      employeeClasses,
      statistic,
      requiredSubInfoPresenter,
      selectedSubscribersIds,
      cartId,
      isRemoveSubscriberModalOpen,
      selectedSubscriber,
      editPrimaryModalStore,
      manageDependentsModalStore,
      updateDependents,
      removeSubscriberById,
      updateSelectedPlan,
      updateClassAndResetProductSelection,
      getSubscribersList,
      submitElection,
      updateElectionChanges,
      updateCurrentPageNumber,
      updateSearchValue,
      checkElection,
      removeCurrentSelectedSubscriber,
      setCurrentSubscriberSelected,
      getStatistic,
      subscribersTotalElementsRange,
      openRemoveSubscriberModal,
      closeRemoveSubscriberModal,
      editPrimaryInfo,
      manageDependents,
      uncompleteElections,
    },
    inviteModalStore: {
      isInviteModalOpen,
      isWarningModalOpen: isInviteWarningModalOpen,
      closeInviteModal,
      closeWarningModal: closeInviteWarningModal,
      inviteUsers,
      setGroupId,
    },
    subscriberStore: { isPeriodNotEditable, currentSubscriber, getLinkToPortfolioDetails },
    productLineRateModalStore: {
      modalLoading,
      isProductLineModalOpen,
      dynamicForm,
      form,
      subscriber: currentPLineModalSubscriber,
      openProductLineModal,
      closeProductLineModal,
      updateSubscriberWithPLineFormAnswers,
    },
    domainStoreV2: { domainHeader, availableClasses },
  } = useRootStore();

  const debouncedValue = useDebounce<string>(searchName, 500);

  const convertedSubscribers = subscribers?.map((subscriber: Subscriber) => toJS(subscriber)).slice();
  const convertedPeriod = toJS(enrollmentPeriod);
  const convertedProducts = products?.map((product: ProductInfo) => toJS(product)).slice();
  const convertedEeClasses = employeeClasses.map((eeClassInfo: EeClass) => toJS(eeClassInfo)).slice();
  const subRates = toJS(statistic)?.subRates;
  const convertedSelectedSubscribersIds = selectedSubscribersIds?.map((subscriberId: string) => subscriberId).slice();
  const convertedPLineModalSubscriber = toJS(currentPLineModalSubscriber);
  const convertedCurrentSubscriber = toJS(currentSubscriber);
  const convertedDomain = toJS(domainHeader);
  const convertedRemovedSubscriber = toJS(selectedSubscriber);
  const convertedRequiredSubInfo = requiredSubInfoPresenter ? toJS(requiredSubInfoPresenter) : undefined;
  const classes = createStyles();

  const classesAvailableForSelection = getClassesAvailableForSelection(convertedEeClasses, availableClasses);

  // Effects
  useEffect(() => {
    if (!firstRender) updateSearchValue(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    // Legacy magic, rework the one who knows why it works
    const periodGroupId = convertedPeriod.groupId;
    if (periodGroupId) setGroupId(periodGroupId);
  }, [convertedPeriod.groupId]);

  const getSubscribersPage = (): void => {
    void getSubscribersList(
      groupId,
      periodId,
      sortBy,
      sortOrder,
      filter,
      searchValue,
      statusesFilter,
      classesFilter,
      currentPage,
    );
  };

  useEffect(() => {
    if (!firstRender) {
      getSubscribersPage();
    }
  }, [sortBy, sortOrder, filter, currentPage, searchValue]);

  const openWarningModal = (): void => {
    setIsEndOpenEnrollmentModalOpen(true);
    void checkElection(groupId, periodId);
  };

  const closeWarningModal = (): void => {
    setIsEndOpenEnrollmentModalOpen(false);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchName(e.target.value);
  };

  const onCompleteEnrollment = async (): Promise<void> => {
    smoothScrollToTop();
    await submitElection(groupId, periodId);
    history.push(`/checklist/${periodId}`);
    closeWarningModal();
  };

  const onBackRedirect = (): void => {
    history.push(`/checklist/${periodId}`);
    closeWarningModal();
  };

  const onSavePrimaryInfo = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await editPrimaryModalStore.saveSubscribersChanges(periodId, async (): Promise<void> => {
      await getStatistic(groupId, periodId, false);
    });
    getSubscribersPage();
  };

  const onChangePageNumber = (newNumber: number): void => {
    updateCurrentPageNumber(newNumber);
    smoothScrollToTop();
  };

  const onCloseInviteModal = (): void => {
    closeInviteModal();
  };

  const onSaveProductLineRateInfo = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (convertedPLineModalSubscriber) {
      await updateSubscriberWithPLineFormAnswers(periodId, convertedPLineModalSubscriber);
      void getStatistic(groupId, periodId, false); // get updated subRates to display in productLine card in Subscriber
      getSubscribersPage();
    }
  };

  const onCloseManageDependentsModal = (): void => {
    manageDependentsModalStore.closeManageDependentsModal();
    void getStatistic(groupId, periodId, false);
    getSubscribersPage();
  };

  const isEmployeeChecked = (subId: string): boolean => {
    return convertedSelectedSubscribersIds.includes(subId);
  };

  const makeEmployeeChecked = (subId: string): void => {
    if (isEmployeeChecked(subId)) {
      removeCurrentSelectedSubscriber(subId);
    } else {
      setCurrentSubscriberSelected(subId);
    }
  };

  const showDetails = (productLineId: ProductLineId, subscriber: Subscriber): void => {
    const link = getLinkToPortfolioDetails(subscriber, productLineId, convertedProducts, cartId, periodId);
    history.push(link);
  };

  const subscribersList = convertedSubscribers?.map((subscriber: Subscriber): JSX.Element => {
    return (
      <TableRow key={subscriber.id} className={classes.tableRow}>
        <TableCell className={classes.tableCell}>
          <SubscriberCard
            subscriber={createSubscriberPresenter(subscriber)}
            productLineRates={subRates?.[subscriber.id] || []}
            products={convertedProducts}
            subscriberStatus={<SubscriberStatusWithInvitationDate subscriber={subscriber} />}
            isAddedToBatchSelection={isEmployeeChecked(subscriber.id)}
            subscriberClassesMap={{
              // Exclude the option to remove selected classes if the election is completed and classes are required
              showFullClassOptions: !(isElectionCompleted && convertedDomain.properties?.eeClassesRequired),
              employeeClasses: classesAvailableForSelection,
            }}
            callbacksMap={{
              editSubscriber: editPrimaryInfo,
              manageDependents,
              removeSubscriber: openRemoveSubscriberModal,
              updateDependentsChoice: (dependentsChoice: DependentsChoice): void => {
                updateDependents(groupId, dependentsChoice);
              },
              updateProduct: (selectedPlanInfo: UpdatedPlanInfo): void => {
                updateSelectedPlan(selectedPlanInfo); // Update plan choice on FE
                void updateElectionChanges(groupId); // Send updated subs to BE
              },
              onPLineRateChange: (formId: string, productId: string): void => {
                void openProductLineModal(subscriber, formId, productId);
              },
              addToBatchSelection: makeEmployeeChecked,
              updateClass: (className: string): void => {
                updateClassAndResetProductSelection(subscriber.id, className); // Update class selection on FE
                void updateElectionChanges(groupId); // Send updated subs to BE
              },
              onShowDetails: (productLineId: ProductLineId): void => {
                showDetails(productLineId, subscriber);
              },
            }}
            requiredSubInfo={convertedRequiredSubInfo}
          />
        </TableCell>
      </TableRow>
    );
  });

  return (
    <>
      {!loading && isElectionCompleted && !isPeriodNotEditable && (
        <ConfirmationBlob
          title="This step has been completed"
          message={
            <>
              The employee elections have been completed and saved. <br /> If you want to make changes, you can click on
              the button below.
            </>
          }
          btnText="Change Elections"
          onConfirm={uncompleteElections}
        />
      )}
      <ElectionsContentWrapperStyled $isElectionCompleted={isElectionCompleted || false}>
        <ElectionsStickyBarStyled>
          <HeaderSection className={classes.headerRow}>
            <ElementsQuantityStyled>
              <QuantityStyled>
                {`${subscribersTotalElementsRange(currentPage, totalElements)} / ${totalElements} `}
              </QuantityStyled>
              employees on the page
            </ElementsQuantityStyled>
            <ElectionSavingStatus
              status={savingStatus}
              resave={(): void => {
                void updateElectionChanges(groupId);
              }}
            />
            <SearchInputWrapperStyled>
              <InputText
                width="25.8rem"
                value={searchName}
                htmlType="search"
                onChange={onSearch}
                placeholder="Employee name"
              />
            </SearchInputWrapperStyled>
          </HeaderSection>
          <ElectionsEmployeesHeader />
        </ElectionsStickyBarStyled>
        <Table
          id={SUBSCRIBERS_TABLE_ID}
          className={classes.electionsTableWrapper}
          loading={initialLoading}
          totalElements={totalElements}
          maxHeight="100%"
          pagination={{
            onPageChange: onChangePageNumber,
            maxPage: totalPages || 1,
            tablePage: currentPage,
            showPageInput: true,
          }}
        >
          <TableBody className={classes.subscribersTableBody}>{subscribersList}</TableBody>
        </Table>

        {!loading && !initialLoading && (
          // Modals
          <>
            <EndOpenEnrollmentModal
              show={isEndOpenEnrollmentModalOpen}
              onClose={(): void => {
                closeWarningModal();
                // Product lines that are not selected will be marked as waived on the backend.
                // Subscribers will be considered invalid if required waiver fields are left unanswered.
                // Retrieve an updated subscribers list with unselected product lines marked as waived and updated statistics
                getSubscribersPage();
                void getStatistic(groupId, periodId, false);
              }}
              onAction={onCompleteEnrollment}
            />
            <EditPrimaryModal
              onAction={onSavePrimaryInfo}
              isEditable={!isPeriodNotEditable}
              modalStore={editPrimaryModalStore}
            />
            <ManageDependentsModal
              modalStore={manageDependentsModalStore}
              subscriber={convertedCurrentSubscriber}
              onClose={onCloseManageDependentsModal}
            />
            <InviteModal show={isInviteModalOpen} onClose={onCloseInviteModal} onAction={inviteUsers} />
            <ActionModal
              show={isInviteWarningModalOpen}
              onClose={closeInviteWarningModal}
              title="New statuses will be updated in a minute."
              modalType={ModalType.NOTIFICATION}
            />
            <ActionModal
              show={isRemoveSubscriberModalOpen}
              onClose={closeRemoveSubscriberModal}
              onAction={(): void => {
                if (convertedRemovedSubscriber?.id) {
                  void removeSubscriberById(groupId, convertedRemovedSubscriber.id, periodId);
                }
                closeRemoveSubscriberModal();
              }}
              title="Remove Subscriber"
              subtitle={
                <span>
                  You are about to remove
                  <b className={classes.removedSubName}>
                    {convertedRemovedSubscriber?.common.firstName} {convertedRemovedSubscriber?.common.lastName}
                  </b>
                  from the subscribers list.
                  <br /> This action cannot be undone.
                </span>
              }
              modalType={ModalType.DELETE}
            />
            <DynamicFormModalDEPRECATED
              show={isProductLineModalOpen}
              onClose={closeProductLineModal}
              onAction={onSaveProductLineRateInfo}
              dynamicForm={dynamicForm}
              form={form}
              loading={modalLoading}
              isEditable
              modalMaxWidth="90rem"
            />
          </>
        )}
      </ElectionsContentWrapperStyled>
      <PageFooter
        onBack={onBackRedirect}
        onComplete={openWarningModal}
        backButtonText="Back"
        completeButtonText="End open enrollment"
        disableCompleteButton={
          !isElectionWithSubs ||
          loading ||
          isElectionCompleted ||
          isPeriodNotEditable ||
          savingStatus !== 'SUCCESSFUL_SAVING'
        }
        completeDisabledReason={!isElectionWithSubs ? 'You can not end open enrollment without subscribers.' : ''}
      />
    </>
  );
});

export default EditableElectionsContent;
